import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './AllEducationPopup.css';

const AllEducationPopup = ({ isOpen, onClose, userEducation, onEditEducation, onRemoveEducation, onAddEducation }) => {
  const [education, setEducation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEducation(userEducation || []);
    }
  }, [isOpen, userEducation]);

  const handleRemoveEducation = async (educationId) => {
    if (window.confirm('Are you sure you want to remove this education entry?')) {
      try {
        const response = await ApiService.delete(`/education/${educationId}`);
        if (response.success) {
          setEducation(prev => prev.filter(edu => edu.id !== educationId));
          onRemoveEducation(educationId);
        }
      } catch (error) {
        console.error('Error removing education:', error);
        alert('Failed to remove education. Please try again.');
      }
    }
  };

  const formatDateRange = (startDate, endDate, isCurrent) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    const end = isCurrent ? 'Present' : formatDate(endDate);
    
    return `${start} - ${end}`;
  };

  if (!isOpen) return null;

  return (
    <div className="all-education-popup-overlay">
      <div className="all-education-popup">
        <div className="all-education-popup-header">
          <h3>All Education</h3>
          <div className="header-actions">
            <button 
              className="add-education-btn"
              onClick={onAddEducation}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Education
            </button>
            <button className="close-btn" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <div className="all-education-popup-content">
          {isLoading ? (
            <div className="education-loading">
              <div className="education-card-shimmer">
                <div className="education-header-shimmer"></div>
                <div className="education-content-shimmer"></div>
              </div>
            </div>
          ) : education.length > 0 ? (
            <div className="education-list">
              {education.map((edu) => (
                <div key={edu.id} className="education-card">
                  <div className="education-header">
                    <div className="education-title-section">
                      {edu.institution_logo && (
                        <img 
                          src={edu.institution_logo} 
                          alt={`${edu.institution} logo`}
                          className="institution-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div className="education-title-content">
                        <h4 className="education-title">{edu.degree}</h4>
                        <p className="education-institution">{edu.institution}</p>
                      </div>
                    </div>
                    <div className="education-actions">
                      <button
                        className="edit-education-btn"
                        onClick={() => onEditEducation(edu)}
                        title="Edit education"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="remove-education-btn"
                        onClick={() => handleRemoveEducation(edu.id)}
                        title="Remove education"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="education-content">
                    <div className="education-details">
                      <div className="education-field">
                        <span className="field-label">Field of Study:</span>
                        <span className="field-value">{edu.field_of_study}</span>
                      </div>
                      
                      <div className="education-dates">
                        <span className="date-label">Duration:</span>
                        <span className="date-value">
                          {formatDateRange(edu.start_date, edu.end_date, edu.is_current)}
                        </span>
                      </div>

                      {edu.gpa && (
                        <div className="education-gpa">
                          <span className="gpa-label">GPA:</span>
                          <span className="gpa-value">{edu.gpa}</span>
                        </div>
                      )}

                      {edu.location && (
                        <div className="education-location">
                          <span className="location-label">Location:</span>
                          <span className="location-value">{edu.location}</span>
                        </div>
                      )}
                    </div>

                    {edu.description && (
                      <div className="education-description">
                        <p>{edu.description}</p>
                      </div>
                    )}

                    {edu.achievements && edu.achievements.length > 0 && (
                      <div className="education-achievements">
                        <h5>Achievements:</h5>
                        <ul>
                          {edu.achievements.map((achievement, index) => (
                            <li key={index}>{achievement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-education-state">
              <div className="no-education-content">
                <h4>No Education Added Yet</h4>
                <p>Start building your academic profile by adding your educational background.</p>
                <button 
                  className="add-education-cta"
                  onClick={onAddEducation}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Add Your First Education
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllEducationPopup;
